import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import DealerUserAccountsTable from './components/DealerUserAccountsTable';
import UserAccountsForm from '../user-definitions/user-accounts/components/UserAccountsForm';
import { useUserAccountsData } from '../user-definitions/user-accounts/hooks/useUserAccountsData';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useGetUserParamsQuery } from '../../../api';
import { useSelector } from 'react-redux';
import { selectUserParams } from '../../../store/slices/userParamsSlice';

const UserAccounts = () => {
    const { th } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedUser, setSelectedUser] = useState(null);
    const [displayModal, setDisplayModal] = useState(false);
    const [currentFormData, setCurrentFormData] = useState(null);
    const toastRef = useRef(null);

    const { data: response } = useGetUserParamsQuery();
    const userParamsFromStore = useSelector(selectUserParams);
    const userParams = userParamsFromStore || response || {};
    const addrId = userParams.addrId;
    const dealerId = userParams.dealerId;
    const dealerFullName = userParams.dealerFullName;

    // URL parametrelerinden başlangıç filtrelerini al
    const initialFiltersFromUrl = useMemo(() => {
        const firstName = searchParams.get('firstName') || '';
        const lastName = searchParams.get('lastName') || '';
        
        return {
            firstName,
            lastName,
            activeOnly: null
        };
    }, [searchParams]);

    // URL'den gelip gelmediğini kontrol et
    const [isFromUrlParams, setIsFromUrlParams] = useState(false);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);

    const {
        users,
        loading,
        filters,
        globalFilterValue,
        onGlobalFilterChange,
        handleSave,
        handleRemove,
        usageTypeOptions,
        totalCount,
        lazyParams,
        onPage,
        onSort,
        handleAdvancedFilterChange,
        handleResetFilters,
        activeFilters,
        error,
        refetch,
        hasFormDataChanged,
        setFormOriginalData
    } = useUserAccountsData({ dealerAddressId: Number(addrId) });

    const filterFields = useMemo(
        () => [
            {
                field: 'firstName',
                headerName: th('userAccounts.firstName'),
                type: 'text',
                placeholder: th('userAccounts.enterFirstName')
            },
            {
                field: 'lastName',
                headerName: th('userAccounts.lastName'),
                type: 'text',
                placeholder: th('userAccounts.enterLastName')
            },
            {
                field: 'activeOnly',
                headerName: th('userAccounts.activeOnly'),
                type: 'boolean'
            }
        ],
        [th]
    );

    const handleEdit = (rowData) => {
        setSelectedUser(rowData);
        setFormOriginalData(rowData);
        setDisplayModal(true);
    };

    const handleNew = () => {
        setSelectedUser(null);
        setFormOriginalData(null);
        setDisplayModal(true);
    };

    const handleHide = () => {
        setDisplayModal(false);
        setSelectedUser(null);
        setCurrentFormData(null);
        setFormOriginalData(null);
    };

    const onFormSubmit = async (formData) => {
        try {
            const result = await handleSave(formData);
            if (result?.success) {
                toastRef.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: selectedUser ? th('userAccounts.updateSuccess') : th('userAccounts.createSuccess'),
                    life: 3000
                });
                handleHide();
            }
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('userAccounts.saveError'),
                life: 5000
            });
        }
    };

    const onFormRemove = async (user) => {
        try {
            const result = await handleRemove(user);
            if (result?.success) {
                toastRef.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('userAccounts.deleteSuccess'),
                    life: 3000
                });
                handleHide();
            }
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('userAccounts.deleteError'),
                life: 5000
            });
        }
    };

    const checkFormDataChanged = () => {
        return hasFormDataChanged(currentFormData);
    };

    const handleFormDataChange = (formData) => {
        setCurrentFormData(formData);
    };

    // URL'den gelen parametrelerle filtreyi otomatik uygula
    useEffect(() => {
        const firstName = searchParams.get('firstName');
        const lastName = searchParams.get('lastName');
        
        if (firstName || lastName) {
            setIsFromUrlParams(true);
            handleAdvancedFilterChange({
                firstName: firstName || '',
                lastName: lastName || '',
                activeOnly: null
            });
            
            // URL parametrelerini temizle (sadece bir kez uygulansın)
            setSearchParams({});
        }
    }, []);

    // Kullanıcı listesi geldiğinde, eğer tek kullanıcı varsa ve URL'den geldiyse otomatik aç
    useEffect(() => {
        if (isFromUrlParams && !hasAutoOpened && users && users.length === 1 && !loading) {
            setHasAutoOpened(true);
            handleEdit(users[0]);
        }
    }, [users, loading, isFromUrlParams, hasAutoOpened]);

    return (
        <div className="card">
            <Toast ref={toastRef} />

            <div className="flex justify-content-between align-items-center mb-4">
                <div className="flex align-items-center gap-3">
                    <h1 className="m-0">{th('userAccounts.title')}</h1>
                </div>
            </div>

            {error && (
                <Message
                    severity="error"
                    className="mb-3 w-full"
                    content={
                        <div className="flex align-items-center justify-content-between w-full">
                            <div className="flex align-items-center">
                                <i className="pi pi-times-circle mr-2"></i>
                                <span>
                                    {th('userAccounts.loadError')}: {error.message}
                                </span>
                            </div>
                            <button className="p-button p-button-text p-button-sm" onClick={() => refetch()}>
                                <i className="pi pi-refresh mr-1"></i>
                                {th('common.retry')}
                            </button>
                        </div>
                    }
                />
            )}

            <div className="mb-2">
                <GenericTableFilter 
                    filterFields={filterFields} 
                    initialFilters={initialFiltersFromUrl} 
                    initiallyOpen={isFromUrlParams}
                    onApplyFilters={handleAdvancedFilterChange} 
                    onResetFilters={handleResetFilters} 
                    className="advanced-filter-container" 
                />
            </div>

            <DealerUserAccountsTable
                users={users}
                loading={loading}
                filters={filters}
                globalFilterValue={globalFilterValue}
                onGlobalFilterChange={onGlobalFilterChange}
                onEdit={handleEdit}
                onNew={handleNew}
                totalCount={totalCount}
                lazyParams={lazyParams}
                onPage={onPage}
                onSort={onSort}
            />

            <UserAccountsForm
                visible={displayModal}
                onHide={handleHide}
                onSubmit={onFormSubmit}
                onRemove={onFormRemove}
                selectedUser={selectedUser}
                loading={loading}
                usageTypeOptions={usageTypeOptions}
                isEditMode={!!selectedUser}
                hasFormDataChanged={checkFormDataChanged}
                onFormDataChange={handleFormDataChange}
                lockedDealerId={dealerId}
                lockedDealerName={dealerFullName}
                lockedAddressId={addrId}
                lockDealerFields
            />
        </div>
    );
};

export default UserAccounts;
